/**
 * API Configuration
 *
 * Handles API base URL configuration for different environments.
 * In development, Vite's proxy handles /api/* requests.
 * In production, we need to call the Workers API directly.
 */

import { useWorkspaceStore } from '@/client/stores/workspace-store';

// Get the API base URL from environment or use empty string for relative URLs
// VITE_API_URL should be set to the Workers URL in production builds
export const API_BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/**
 * Build a full API URL
 * @param path - API path starting with /api/
 * @returns Full URL to the API endpoint
 */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

/**
 * Get the active workspace ID from the store
 * Returns null if no workspace is selected
 */
function getActiveWorkspaceId(): string | null {
  return useWorkspaceStore.getState().activeWorkspaceId;
}

export interface ApiFetchOptions extends RequestInit {
  /**
   * Skip adding the X-Workspace-Id header.
   * Useful for endpoints that don't require workspace context (e.g., /api/workspaces list)
   */
  skipWorkspaceHeader?: boolean;
}

/**
 * Fetch wrapper that handles API base URL and workspace context
 * @param path - API path starting with /api/
 * @param options - Fetch options with optional skipWorkspaceHeader
 * @returns Fetch response
 */
export function apiFetch(path: string, options?: ApiFetchOptions): Promise<Response> {
  const { skipWorkspaceHeader, ...fetchOptions } = options ?? {};

  // Build headers with workspace context
  const headers = new Headers(fetchOptions.headers);

  // Add workspace header unless explicitly skipped
  if (!skipWorkspaceHeader) {
    const workspaceId = getActiveWorkspaceId();
    if (workspaceId) {
      headers.set('X-Workspace-Id', workspaceId);
    }
  }

  return fetch(apiUrl(path), {
    ...fetchOptions,
    headers,
    credentials: 'include', // Include cookies for auth
  });
}
